import { alpha, Box, Typography, useTheme } from '@mui/material';
import { _ } from '/@/locale';
import ApplicationPill from './application-pill';
import type { ForecastRow } from './types';

interface ForecastTableProps {
    /** Header label for the first column ("Hora" or "Día"). */
    firstColLabel: string;
    rows: ForecastRow[];
    /** Temperature unit shown in the "T" header, e.g. "°C". */
    tempUnit: string;
    /** Speed unit shown in the "V" header, e.g. "km/h". */
    speedUnit: string;
}

const GRID = 'repeat(6, 1fr)';

/** Header cell with an optional lighter-weight unit in parentheses (e.g. "T (°C)"). */
const HeaderCell = ({ label, unit, color }: { label: string; unit?: string; color: string }) => (
    <Typography variant='caption' component='span' fontWeight='bold' color={color}>
        {label}
        {unit && (
            <Typography variant='caption' component='span' fontWeight={400}>
                {' '}
                ({unit})
            </Typography>
        )}
    </Typography>
);

const ForecastTable = ({ firstColLabel, rows, tempUnit, speedUnit }: ForecastTableProps) => {
    const theme = useTheme();
    const textColor = theme.palette.primary.main;
    const divider = alpha(theme.palette.primary.main, 0.12);

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: GRID,
                    alignItems: 'center',
                    columnGap: 1,
                    height: 40,
                    px: 1.5,
                }}
            >
                <HeaderCell label={firstColLabel} color={textColor} />
                <HeaderCell label='T' unit={tempUnit} color={textColor} />
                <HeaderCell label='PP' color={textColor} />
                <HeaderCell label='HR' color={textColor} />
                <HeaderCell label='V' unit={speedUnit} color={textColor} />
                <HeaderCell label={_('Aplicaciones')} color={textColor} />
            </Box>

            {rows.map((row) => (
                <Box
                    key={row.id}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: GRID,
                        alignItems: 'center',
                        columnGap: 1,
                        minHeight: 48,
                        px: 1.5,
                        borderTop: `1px solid ${divider}`,
                        color: textColor,
                    }}
                >
                    <Typography variant='body2'>{row.label}</Typography>
                    <Typography variant='body2'>{row.temp}</Typography>
                    <Typography variant='body2'>{row.rain}</Typography>
                    <Typography variant='body2'>{row.humidity}</Typography>
                    <Typography variant='body2'>{row.wind}</Typography>
                    <Box>
                        <ApplicationPill risk={row.risk} />
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export default ForecastTable;
